// vitest からは Nuxt auto-import が効かないため明示 import に。
import { readonly, ref } from 'vue'
import {
  confirmForgotPasswordRequest,
  deleteMe,
  forgotPasswordRequest,
  signIn,
  signOutRequest,
  validateSignUpAttributes,
} from './api/auth'
import type { ResponseError } from './custom-fetch'
import { resetGroupState } from './group'
import { resetMeState } from './me'
import { LocalStorage } from '~/utils/local-storage'
import { storage } from '~/utils/session-storage'

// Auth はサーバから受け取った session を local storage に保管するための
// UI 側の表現で、API レスポンスそのものではない (expiresIn 秒 → expiresAt epoch ms)。
export interface Auth {
  sessionId: string
  expiresAt: number
}

const EXPIRED_BUFFER_MINUTE = 5
const { getItem, removeItem, setAndGetItem, clear: clearLocalStorage } = LocalStorage<Auth>('auth')
const { clear: clearSessionStorage } = storage<Auth>()
const auth = ref(getItem({}))

const get = async (signInId: string, password: string) => {
  const data = await signIn(signInId, password)

  // 前ユーザーの state を破棄(session storage 全消去は entry フローの進行状態を壊すため不可)。
  // me は本人 proxyGroupMemberId を保持し、残ると代理対象者(getTargets)の本人が前ユーザーの
  // pgm になって退会済み等の他人 pgm を target 送信し 403 になるため、group と同様に破棄する。
  resetGroupState()
  resetMeState()

  // JSの世界ではミリ秒
  const expiresIn = data.expiresIn * 1000
  auth.value = setAndGetItem(
    {
      sessionId: data.sessionId,
      expiresAt: expiresIn + new Date().getTime(),
    } as Auth,
    {}
  )
  return readonly(auth.value)
}

type SignUpAttributeParams = {
  name?: string
  nameRuby?: string
  prefectures?: string
  municipalities?: string
  street?: string
  address?: string
  building?: string
  municipalitiesRuby?: string
  streetRuby?: string
  addressRuby?: string
  buildingRuby?: string
}

const validateToSignUp = async ({
  name,
  nameRuby,
  prefectures,
  municipalities,
  street,
  address,
  building,
  municipalitiesRuby,
  streetRuby,
  addressRuby,
  buildingRuby,
}: SignUpAttributeParams): Promise<ResponseError> => {
  return await validateSignUpAttributes({
    name,
    nameRuby,
    prefectures,
    municipalities,
    street,
    address,
    building,
    municipalitiesRuby,
    streetRuby,
    addressRuby,
    buildingRuby,
  })
}

const _delete = async () => {
  await deleteMe(auth.value.sessionId)
  removeAllStorage()
}

const forgotPassword = async (signInId: string) => {
  return ref(await forgotPasswordRequest(signInId))
}
const confirmForgotPassword = async (
  signInId: string,
  newPassword: string,
  resetToken?: string
) => {
  return ref(await confirmForgotPasswordRequest(signInId, newPassword, resetToken))
}

const signOut = async () => {
  // セッション失効/アカウント削除済(401)等でサーバ応答が失敗しても、ローカルのサインアウトは
  // 必ず完了させる。サインアウト状態を作り出すことは可能なので、応答の成否に関わらず storage を消す。
  try {
    await signOutRequest(auth.value.sessionId)
  } finally {
    removeAllStorage()
  }
}

const removeAllStorage = () => {
  removeItem()
  auth.value = getItem({})
  clearSessionStorage()
}

const clear = () => {
  clearLocalStorage()
  auth.value = getItem({})
}

// todo: replace with day.js
const isExpired = (currentDate: Date) => {
  if (!auth.value.expiresAt) return true

  const baseDate = new Date(
    currentDate.setMinutes(currentDate.getMinutes() + EXPIRED_BUFFER_MINUTE)
  )
  const expiryDate = new Date(auth.value.expiresAt)

  const isExpired = baseDate > expiryDate

  // todo: layoutで良いのでは？
  // if (isExpired) {
  //   const { setAlert } = useAlert();
  //   setAlert('サインインの有効期限が切れました。\n再度サインインが必要です。');
  // }

  return isExpired
}

const isAuthenticated = (currentDate: Date) => {
  const isAuthenticated = !!auth.value.sessionId && !isExpired(currentDate)
  return isAuthenticated
}

export const useAuth = () => {
  return {
    auth: readonly(auth),
    isAuthenticated,
    isExpired,
    get,
    delete: _delete,
    validateToSignUp,
    forgotPassword,
    confirmForgotPassword,
    signOut,
    clearSessionStorage,
    clear,
  }
}
